import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, CardDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getCardTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): CardDesignTokens => ({
  container: {
    'background-color': theme.tokens.colors.background.primary.value,
    'border-color': theme.tokens.colors.border.subtle.value,
    'border-width': px(1),
    'border-style': 'solid',
    'border-radius': px(theme.borderRadius.medium),
    'box-shadow': theme.shadows.lowElevation,
  },
  title: {
    'text-color': theme.tokens.colors.text.primary.value,
    'padding-top': px(theme.spacing.M),
    'padding-bottom': px(theme.spacing.S),
    'padding-left': px(theme.spacing.M),
    'padding-right': px(theme.spacing.M),
    'font-family': theme.typography.bodyMedium.fontFamily,
    large: {
      'font-size': theme.typography.heading1.fontSize,
      'line-height': theme.typography.heading1.lineHeight,
      'font-weight': String(theme.typography.heading1.fontWeight),
    },
    small: {
      'font-size': theme.typography.heading3.fontSize,
      'line-height': theme.typography.heading3.lineHeight,
      'font-weight': String(theme.typography.heading3.fontWeight),
    },
  },
  selected: {
    'border-color': theme.tokens.colors.background.neutral.selected.value,
    'box-shadow': theme.halos.focus,
  },
  error: {
    'border-color': theme.tokens.colors.border.error.value,
  },
  body: {
    color: theme.tokens.colors.text.primary.value,
    'padding-top': px(theme.spacing.S),
    'padding-right': px(theme.spacing.M),
    'padding-bottom': px(theme.spacing.M),
    'padding-bottom-with-footer': px(theme.spacing.S),
    'padding-left': px(theme.spacing.M),
  },
  footer: {
    color: theme.tokens.colors.text.primary.value,
    'padding-top': px(theme.spacing.S),
    'padding-right': px(theme.spacing.M),
    'padding-bottom': px(theme.spacing.M),
    'padding-left': px(theme.spacing.M),
  },
  divider: {
    color: theme.tokens.colors.border.subtle.value,
    height: px(1),
    'margin-left': px(theme.spacing.M),
    width: `calc(100% - ${px(theme.spacing.M)} * 2)`,
  },
});

export const getCardTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): CardDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;

  const spacing16 = generic?.spacing?.[16];
  const spacing12 = generic?.spacing?.[12];

  return {
    container: {
      'background-color': semantic?.['background-color']?.surface?.[100],
      'border-color': semantic?.['border-color']?.divider?.light,
      'border-width': generic?.['border-width']?.[1],
      'border-style': 'solid',
      'border-radius': generic?.['border-radius']?.[4],
      'box-shadow': semantic?.shadow?.low,
    },
    title: {
      'text-color': semantic?.['text-color']?.light,
      'padding-top': spacing16,
      'padding-bottom': spacing12,
      'padding-left': spacing16,
      'padding-right': spacing16,
      'font-family': fontFamily,
      large: {
        'font-size': generic?.['font-size']?.[24],
        'line-height': generic?.['line-height']?.[30],
        'font-weight': '400',
      },
      small: {
        'font-size': generic?.['font-size']?.[16],
        'line-height': generic?.['line-height']?.[22],
        'font-weight': '500',
      },
    },
    selected: {
      'border-color': semantic?.['border-color']?.selected,
      'box-shadow': semantic?.halo?.default?.focus,
    },
    error: {
      'border-color': semantic?.['status-color']?.error,
    },
    body: {
      color: semantic?.['text-color']?.light,
      'padding-top': spacing12,
      'padding-right': spacing16,
      'padding-bottom': spacing16,
      'padding-bottom-with-footer': spacing12,
      'padding-left': spacing16,
    },
    footer: {
      color: semantic?.['text-color']?.light,
      'padding-top': spacing12,
      'padding-right': spacing16,
      'padding-bottom': spacing16,
      'padding-left': spacing16,
    },
    divider: {
      color: semantic?.['border-color']?.divider?.heavy,
      height: generic?.['border-width']?.[1],
      'margin-left': spacing16,
      width: spacing16 ? `calc(100% - ${spacing16} * 2)` : undefined,
    },
  };
};
